/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 *
 * Notes:
 * - Stock-only behavior is enabled when Liquid sets data-stock-only="true"
 * - Scope of hiding controlled with data-stock-hide="all" | "fabric-only"
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section?.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section?.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

this.updateAvailability();

// Availability may have changed the checked option,
// so read the selected variant again.
this.selectedOptions = this.getSelectedOptions();
this.variant = this.getSelectedVariant();

// Make the initial price/form match the visible selected variant.
this.updateVariantInput();
this.updatePrice();

this.updateAddToCartButton();
this.updatePerformanceBadge();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();

      this.setAttribute('loaded', '');

      // Load swatch images now that JS is ready — shows shimmer first, then image
      this.querySelectorAll('.swatch--lazy[data-src]').forEach((el) => {
        const src = el.dataset.src;
        const img = new Image();
        img.onload = () => {
          el.style.backgroundImage = `url('${src}')`;
          el.style.backgroundSize = 'cover';
          el.style.backgroundPosition = 'center';
          el.style.animation = 'none';
          el.style.background = `url('${src}') center/cover no-repeat`;
        };
        img.src = src;
      });
    }

    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updatePerformanceBadge();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? (this.addBtn.dataset.addToCartText || this.addBtn.textContent)
        : unavailableStr;
    }

    updatePerformanceBadge() {
      this.performanceBadge = this.performanceBadge || this.querySelector('.fabric-performance-badge');
      if (!this.performanceBadge) return;

      const showBadge = this.variant && this.data.formatted?.[this.variant.id]?.performance === true;
      this.performanceBadge.style.display = showBadge ? 'flex' : 'none';
      this.performanceBadge.style.alignItems = 'center';
    }

    hideOptionNode(optionEl) {
      if (optionEl.classList.contains('js-option') && optionEl.tagName === 'INPUT') {
        const label = optionEl.nextElementSibling;
        optionEl.classList.add('variant-option--hidden');
        if (label) label.classList.add('variant-option--hidden');
        if (optionEl.checked) optionEl.checked = false;
      }
      if (optionEl.classList.contains('custom-select__option')) {
        optionEl.classList.add('variant-option--hidden');
        optionEl.setAttribute('aria-hidden', 'true');
        optionEl.hidden = true;
        if (optionEl.getAttribute('aria-selected') === 'true') {
          optionEl.setAttribute('aria-selected', 'false');
        }
      }
    }

    showOptionNode(optionEl) {
      if (optionEl.classList.contains('js-option') && optionEl.tagName === 'INPUT') {
        const label = optionEl.nextElementSibling;
        optionEl.classList.remove('variant-option--hidden');
        if (label) label.classList.remove('variant-option--hidden');
      }
      if (optionEl.classList.contains('custom-select__option')) {
        optionEl.classList.remove('variant-option--hidden');
        optionEl.removeAttribute('aria-hidden');
        optionEl.hidden = false;
      }
    }

    shouldHideOnSelector(selector) {
      if (this.dataset.stockOnly !== 'true') return false;
      if (typeof window !== 'undefined' && window.__ML_STOCK_ONLY__ === false) return false;

      const scope = (this.dataset.stockHide || 'all').toLowerCase();
      if (scope === 'fabric-only') {
        return (selector?.dataset.option || '').toLowerCase().includes('fabric');
      }
      return true;
    }

    static updateOptionAvailability(optionEl, exists, available, stockOnly = false, ctx) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;

      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      const isDropdownItem = el.classList.contains('custom-select__option');

      if (isDropdownItem) {
        const em = el.querySelector('em');
        if (em) em.hidden = available;
        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        if (el.nextElementSibling) el.nextElementSibling.title = unavailableText;
      } else {
        if (el.nextElementSibling) el.nextElementSibling.removeAttribute('title');
      }

      if (stockOnly) {
        const shouldShow = exists && available;
        if (shouldShow) ctx.showOptionNode(el);
        else ctx.hideOptionNode(el);
      }
    }

    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) currVariant = { options: this.selectedOptions };

      const stockOnlyGlobal = this.dataset.stockOnly === 'true';

      const markForSelector = (selector, optionEl, exists, available) => {
        const stockOnlyHere = stockOnlyGlobal && this.shouldHideOnSelector(selector);
        VariantPicker.updateOptionAvailability(optionEl, exists, available, stockOnlyHere, this);
      };

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          const selector = optionEl.closest('.option-selector');
          VariantPicker.updateOptionAvailability(
            optionEl,
            false,
            false,
            stockOnlyGlobal && this.shouldHideOnSelector(selector),
            this
          );
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;
            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) matchCount += 1;
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') return opt.dataset.value === variant.options[selectorIndex];
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) markForSelector(selector, optionEl, true, !!variant.available);
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;

            let variantsExist = false;
            let variantsAvailable = false;

            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) matches += 1;
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) variantsAvailable = true;
              }
            });

            markForSelector(selector, option, variantsExist, variantsAvailable);
          });
        });
      }

      this.optionSelectors.forEach((selector) => {
        const isDropdown = selector.dataset.selectorType === 'dropdown';

        if (isDropdown) {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          if (!selected || selected.classList.contains('variant-option--hidden')) {
            const firstVisible = selector.querySelector('.custom-select__option:not(.variant-option--hidden)');
            if (firstVisible) firstVisible.closest('custom-select').selectOption(firstVisible);
          }
        } else {
          const checked = selector.querySelector('input:checked');
          if (!checked || checked.classList.contains('variant-option--hidden')) {
            const firstVisible = selector.querySelector('input.js-option:not(.variant-option--hidden)');
            if (firstVisible) {
              firstVisible.checked = true;
              firstVisible.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else {
            const labelValue = selector.querySelector('.option-selector__label-value');
            if (labelValue) {
              const text = checked.nextElementSibling?.querySelector('.js-value')?.textContent || checked.value;
              labelValue.textContent = text;
            }
          }
        }
      });
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section?.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formatted = this.data.formatted?.[this.variant.id] || {};
        const inventory = formatted.inventory;

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section?.querySelector('.product-title');
          const prodTitle = prodTitleEl ? prodTitleEl.textContent : '';
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          if (backorderProdEl) backorderProdEl.textContent = `${prodTitle}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    // ✅ FIXED: works with .option-selector__label-value (and legacy .js-color-text)
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      if (!(evt.target instanceof HTMLInputElement) || evt.target.type !== 'radio') return;

      const labelValue =
        selector.querySelector('.js-color-text') ||
        selector.querySelector('.option-selector__label-value');

      if (!labelValue) return;

      const valueSpan = evt.target.nextElementSibling?.querySelector('.js-value');
      labelValue.textContent = valueSpan ? valueSpan.textContent : evt.target.value;
    }

    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section?.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    updatePrice() {
      this.price = this.price || this.section?.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        const formatted = this.data.formatted?.[variant.id] || {};
        if (priceCurrentEl && formatted.price) priceCurrentEl.innerHTML = formatted.price.replace(/\.00/, '');
        if (priceWasEl) priceWasEl.innerHTML = formatted.compareAtPrice || '';

        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          if (valueEl && formatted.unitPrice) valueEl.innerHTML = formatted.unitPrice;
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          if (unitPriceEl) unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      const defaultEl = this.price.querySelector('.price__default');
      if (defaultEl) defaultEl.hidden = !this.variant && !this.preSelection;
      const noVar = this.price.querySelector('.price__no-variant');
      if (noVar) noVar.hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = !this.preSelection;
    }

    updateSku() {
      this.sku = this.sku || this.section?.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      if (this.sku.parentNode) this.sku.parentNode.hidden = !skuAvailable;
    }

    updateBarcode() {
      this.barcode = this.barcode || this.section?.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      if (this.barcode.parentNode) this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({}, '', url);
    }

    updateVariantInput() {
      this.forms = this.forms || this.section?.querySelectorAll('.js-product-form, .js-instalments-form') || [];

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        if (!input) return;
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    getSelectedVariant() {
      return this.data.variants.find((v) => v.options.every((val, index) => val === this.selectedOptions[index]));
    }

    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const opt = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (opt) opt.closest('custom-select').selectOption(opt);
        } else {
          const input = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (input) {
            const labelValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (labelValue) labelValue.textContent = value;
            input.checked = true;
            input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

/**
 * Hide dropdown arrow for single-option selects
 */
(function hideSingleOptionArrows() {
  function checkAndHideArrows() {
    const dropdownSelectors = document.querySelectorAll('.option-selector[data-selector-type="dropdown"]');

    dropdownSelectors.forEach((selector) => {
      const selectWrapper = selector.querySelector('custom-select');
      if (!selectWrapper) return;

      const listbox = selectWrapper.querySelector('.custom-select__listbox');
      if (!listbox) return;

      const allOptions = listbox.querySelectorAll('.custom-select__option');
      const visibleOptions = listbox.querySelectorAll('.custom-select__option:not(.variant-option--hidden)');

      const svg = selectWrapper.querySelector('.custom-select__btn svg');
      const btn = selectWrapper.querySelector('.custom-select__btn');

      if (!svg || !btn) return;

      if (allOptions.length === 1 || visibleOptions.length === 1) {
        svg.style.display = 'none';
        btn.style.cursor = 'default';
        btn.style.pointerEvents = 'none';
      } else {
        svg.style.display = '';
        btn.style.cursor = '';
        btn.style.pointerEvents = '';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAndHideArrows);
  } else {
    checkAndHideArrows();
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && (node.tagName === 'CUSTOM-SELECT' || node.querySelector?.('custom-select'))) {
          setTimeout(checkAndHideArrows, 100);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // ✅ safer than document-only
  window.addEventListener('on:variant:change', () => setTimeout(checkAndHideArrows, 50));
})();
